/**
 * Gen3 → Core Client route mapping (PLAPP-777, pre-launch toggle).
 *
 * This (Gen3) app scopes the org via an `?org_id=` query param — or, for the
 * `/orgs/:orgId/...` routes, a path segment. The Core Client app scopes the org
 * via a leading `/{orgId}` path segment instead. So mapping a route here means
 * moving the org id out of the query (or `/orgs` segment) and onto the front of
 * the Core Client path.
 *
 * This is the mirror of the Core Client → Gen3 mapper in
 * `eltoro-core-client-platform` (`utils/gen3Routes.ts`); keep the two in sync.
 * See that repo's ROUTES.md for the canonical table. Unknown / unmapped routes
 * fall back to the Core Client org dashboard.
 */

// Org ids come in several shapes — UUID, 24-char Mongo ObjectId, and short
// base62 (e.g. `vMcpchMTTnpGiSeb2`) — so we must NOT pattern-match the id shape
// to detect an org (see the Core Client app's ROUTES.md). This regex only
// rejects values that could break out of the path segment (`/`, `?`, `#`, `&`,
// …), keeping the `?org_id=` guard without dropping legitimate orgs.
const ORG_ID_SAFE_RE = /^[A-Za-z0-9_-]+$/

/**
 * Resolve the active org id for a Gen3 location: prefer the `?org_id=` query
 * param; otherwise, for `/orgs/:orgId/...` routes, read it from the path.
 */
export function getOrgIdFromLocation(
  pathname: string,
  search: string,
): string | null {
  // Accept any URL-safe org id; only reject values that could inject arbitrary
  // path/query into the Core Client URL.
  const fromQuery = new URLSearchParams(search).get('org_id')
  if (fromQuery && ORG_ID_SAFE_RE.test(fromQuery)) return fromQuery

  // `/orgs/:orgId/...` — the org id is positionally the second segment; don't
  // pattern-match its shape.
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] === 'orgs' && segments[1]) return segments[1]
  return null
}

/**
 * Map a Gen3 pathname to the equivalent Core Client path (without the leading
 * org segment, which `buildCoreClientUrl` prepends).
 */
export function mapGen3PathToCoreClient(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  const [a, b, c, d] = segments

  // Root → Core Client org dashboard.
  if (segments.length === 0) return '/'

  switch (a) {
    case 'campaigns':
      if (b === 'dashboard') {
        // /campaigns/dashboard/order-lines/:olid → standalone order line
        if (c === 'order-lines' && d) return `/orderline/${d}`
        // /campaigns/dashboard/:cid → campaign
        if (c) return `/campaigns/${c}`
        // /campaigns/dashboard → dashboard
        return '/'
      }
      // /campaigns/creative-library[/:id]
      if (b === 'creative-library') {
        return c ? `/creatives/${c}` : '/creatives'
      }
      // /campaigns/create
      if (b === 'create') return '/new-orderline'
      return '/'

    case 'audiences':
      // /audiences/audience-library[/:id]
      if (b === 'audience-library') {
        return c ? `/audiences/${c}` : '/audiences'
      }
      // /audiences/create has no Core Client equivalent — send to the library.
      if (b === 'create') return '/audiences'
      // /audiences/marketplace and anything else → no Core Client equivalent.
      return '/'

    case 'pixel-library':
      // Core Client has no separate pixel / subscription / publisher-subscription
      // routes — every pixel-library subpath lands on the library itself.
      return '/pixel-library'

    case 'advanced-reporting':
      return '/reporting'

    case 'orgs':
      // /orgs/:orgId/billing
      if (c === 'billing') return '/billing'
      // /orgs/:orgId/org-settings, and /orgs/:orgId/child-orgs — Core Client has
      // no separate child-orgs page; they're managed under org settings there.
      if (c === 'org-settings' || c === 'child-orgs') return '/organization'
      return '/'

    case 'search':
      return '/search'

    // theme-info, documentation, internal-api-docs, release-notes, test-page,
    // /reporting placeholders, and anything unknown → dashboard.
    default:
      return '/'
  }
}

/**
 * Build the full Core Client destination URL for a Gen3 location. Moves the org
 * id onto the front of the path to match Core Client's `/{orgId}` scoping.
 *
 * The org id is read from the location (query param or `/orgs/:orgId` path);
 * many Gen3 routes (e.g. `/search`) don't carry `org_id` in the URL, so callers
 * should pass the active org from context as `fallbackOrgId`. If no org id can
 * be resolved at all, falls back to the Core Client root.
 *
 * @param base           Core Client origin, no trailing slash (e.g. https://…eltoro.com)
 * @param pathname       Current Gen3 pathname (window.location.pathname)
 * @param search         Current Gen3 query string (window.location.search)
 * @param fallbackOrgId  Active org id from context, used when the URL lacks one
 */
export function buildCoreClientUrl(
  base: string,
  pathname: string,
  search: string,
  fallbackOrgId?: string | null,
): string {
  const orgId = getOrgIdFromLocation(pathname, search) || fallbackOrgId
  if (!orgId) return `${base}/`

  const path = mapGen3PathToCoreClient(pathname)
  const orgPath = path === '/' ? `/${orgId}` : `/${orgId}${path}`

  // Carry the search term across to the Core Client search page. Gen3 keys it
  // as `keyword` (one is supported); Core Client reads it as `query`.
  if (path === '/search') {
    const keyword = new URLSearchParams(search).get('keyword')
    if (keyword) {
      return `${base}${orgPath}?${new URLSearchParams({ query: keyword })}`
    }
  }
  return `${base}${orgPath}`
}
